/**
 * One-shot sound for in-app notification banners (push-header toasts).
 * Call ringtones use callRingtoneService — this is separate and excludes call banners.
 *
 * iOS unlocks autoplay per HTMLAudioElement. Unlocking a silent WAV elsewhere does
 * NOT unlock banner.wav — we must mute-prime this element on a user gesture.
 */

import bannerWav from '../../assets/ringtones/banner.wav';
import { isIncomingCallRequestPush } from '../pusNotification/fcm/pushCallNotificationPolicy';
import {
  setAmbientAudioSession,
  setTransientAudioSession,
} from '../utils/audioSession';

const CALL_ACTIVITY_TYPES = new Set(['incoming_call_video', 'incoming_call_voice']);

const readPushData = (detail = {}) =>
  detail?.data && typeof detail.data === 'object' ? detail.data : detail;

export const shouldPlayNotificationBannerSound = (rawDetail = {}, normalizedDetail = {}) => {
  const data = readPushData(rawDetail);
  const type = rawDetail?.type || data?.type || '';
  const activityType = normalizedDetail?.activityType || '';

  if (CALL_ACTIVITY_TYPES.has(activityType)) {
    return false;
  }

  if (isIncomingCallRequestPush(data) || isIncomingCallRequestPush({ type })) {
    return false;
  }

  if (String(type).toLowerCase() === 'incoming_call_request') {
    return false;
  }

  return true;
};

class NotificationBannerSoundService {
  constructor() {
    /** @type {HTMLAudioElement | null} */
    this.audio = null;
    this.initialized = false;
    this.unlocked = false;
    /** True while a muted gesture-prime is in flight — skip audible play(). */
    this.priming = false;
  }

  ensureHost() {
    if (typeof document === 'undefined' || !document.body) return null;
    let host = document.getElementById('jh-notification-banner-sound-host');
    if (host) return host;

    host = document.createElement('div');
    host.id = 'jh-notification-banner-sound-host';
    host.setAttribute('aria-hidden', 'true');
    host.style.cssText =
      'position:fixed;width:0;height:0;overflow:hidden;opacity:0;pointer-events:none';
    document.body.appendChild(host);
    return host;
  }

  ensureAudio() {
    if (this.audio) return this.audio;

    const audio = new Audio(bannerWav);
    audio.preload = 'auto';
    audio.volume = 1;
    audio.loop = false;
    audio.playsInline = true;
    audio.setAttribute('playsinline', '');
    audio.setAttribute('webkit-playsinline', '');

    const host = this.ensureHost();
    if (host) host.appendChild(audio);
    this.audio = audio;
    return audio;
  }

  /**
   * Mute-prime banner.wav during a user gesture (no audible blip).
   * Pause + reset before unmuting so swipes/loads never leak sound.
   */
  kickUnlockFromUserGesture() {
    if (typeof window === 'undefined' || this.unlocked || this.priming) return;

    const audio = this.ensureAudio();
    setAmbientAudioSession();
    this.priming = true;

    try {
      audio.muted = true;
      audio.volume = 0;
      try {
        audio.currentTime = 0;
      } catch (_) { }

      const finish = (ok) => {
        try {
          audio.pause();
          audio.currentTime = 0;
        } catch (_) { }
        audio.muted = false;
        audio.volume = 1;
        this.priming = false;
        if (ok) this.unlocked = true;
        setAmbientAudioSession();
      };

      const p = audio.play();
      if (p?.then) {
        p.then(() => finish(true)).catch(() => finish(false));
      } else {
        finish(true);
      }
    } catch (_) {
      audio.muted = false;
      audio.volume = 1;
      this.priming = false;
      setAmbientAudioSession();
    }
  }

  init() {
    if (this.initialized || typeof window === 'undefined') return;
    this.initialized = true;

    const boot = () => {
      this.ensureAudio();

      const onGesture = () => {
        if (this.unlocked) return;
        this.kickUnlockFromUserGesture();
      };
      const opts = { capture: true, passive: true };
      ['pointerdown', 'touchstart', 'click', 'keydown'].forEach((name) => {
        document.addEventListener(name, onGesture, opts);
      });
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', boot, { once: true });
    } else {
      boot();
    }
  }

  async play() {
    if (typeof window === 'undefined') return;
    // Don't interrupt a muted gesture-prime — that would unmute mid-prime and blip.
    if (this.priming) return;

    const audio = this.ensureAudio();

    try {
      setTransientAudioSession();
      audio.muted = false;
      audio.volume = 1;
      audio.pause();
      try {
        audio.currentTime = 0;
      } catch (_) { }
      const restoreAmbient = () => {
        setAmbientAudioSession();
        audio.removeEventListener('ended', restoreAmbient);
        audio.removeEventListener('pause', restoreAmbient);
      };
      audio.addEventListener('ended', restoreAmbient);
      audio.addEventListener('pause', restoreAmbient);
      await audio.play();
      this.unlocked = true;
    } catch (_) {
      setAmbientAudioSession();
      // Autoplay blocked or playback interrupted — non-fatal.
    }
  }
}

export const notificationBannerSoundService = new NotificationBannerSoundService();

export function initNotificationBannerSound() {
  notificationBannerSoundService.init();
}

export function playNotificationBannerSound() {
  return notificationBannerSoundService.play();
}
